import { type ChangeEvent, useCallback, useEffect, useRef } from 'react';

import { useId, useTrackEvent } from 'hooks';
import {
	type GTMItemListId,
	type GTMItemListName,
	useProductListGTMEvents,
} from 'hooks/gtm';
import type { VariantId } from 'models/product';
import type { ProductCard, RelatedProductCard } from 'models/productCard';
import { getProductImage } from 'utils/business-logic';

import CheckboxList, { type Props as BaseProps } from './CheckboxList';

interface Props extends Omit<BaseProps, 'items'> {
	checkedIds: VariantId[];
	gtmItemListId: GTMItemListId;
	gtmItemListName: GTMItemListName;
	onItemChange: (
		e: ChangeEvent<HTMLInputElement & { value: VariantId }>,
	) => void;
	products: (ProductCard | RelatedProductCard)[];
}

/**
 * Maps products to CheckboxList and handles view and select GTM events.
 * Checkbox values are the product variant ids.
 */
export default function ProductCheckboxList({
	checkedIds,
	gtmItemListId,
	gtmItemListName,
	initialVisibleItemsCount,
	products,
	...props
}: Props) {
	const baseId = useId('product-checkbox-list');
	const { sendSelectItemEvent, sendViewItemListEvent } =
		useProductListGTMEvents(gtmItemListId, gtmItemListName);
	const { sendProductClickEvent } = useTrackEvent();

	const sellableProducts = products.filter((product) => product.isSellable);

	const sentIdsRef = useRef(new Set<string>());
	const sendEvent = useCallback(
		(eventItems: Parameters<typeof sendViewItemListEvent>[0]) => {
			const unsent = eventItems.filter(
				(item) =>
					!sentIdsRef.current.has('id' in item ? item.id : item.variantId),
			);
			if (unsent.length === 0) {
				return;
			}
			unsent.forEach((item) => {
				sentIdsRef.current.add('id' in item ? item.id : item.variantId);
			});
			// TODO: pageSize is pointless here since each item has an index.
			sendViewItemListEvent(unsent, unsent.length);
		},
		[sendViewItemListEvent],
	);

	// Send event for the initially visible items.
	useEffect(() => {
		sendEvent(
			sellableProducts
				.slice(0, initialVisibleItemsCount ?? 999)
				.map(({ id }, i) => ({ id, index: i + 1 })),
		);
	}, [initialVisibleItemsCount, sendEvent, sellableProducts]);

	return (
		<CheckboxList
			{...props}
			initialVisibleItemsCount={initialVisibleItemsCount}
			onExpand={() => {
				sendEvent(sellableProducts.map(({ id }, i) => ({ id, index: i + 1 })));
			}}
			items={sellableProducts.map((product, i) => {
				const image = getProductImage(product);
				const energySymbol = product.energySymbols?.[0];

				return {
					title: product.title,
					text: product.brand?.title,
					href: product.url,
					imageSrc: image?.src,
					imageAlt: image?.alt,
					price: product.displayPrice,
					energySymbolSrc: energySymbol?.src,
					energySymbolAlt: energySymbol?.alt,
					productSheetLink: product.productSheetLink,
					productSheetLinkDescription: product.productSheetLinkDescription,
					id: `${baseId}-${product.id}`,
					value: product.id,
					checked: checkedIds.includes(product.id),
					onLinkClick: () => {
						sendProductClickEvent(product.id);
						sendSelectItemEvent(product, i);
					},
				};
			})}
		/>
	);
}
ProductCheckboxList.displayName = 'ProductCheckboxList';
